// Shared trust & safety constants used by both client and server so the report
// UI, the API validation, and the admin queue all speak the same vocabulary.
// Supports Apple App Store Guideline 1.2 (user-generated content): a report
// mechanism, a filter for objectionable material, and a block capability.

export const REPORT_TARGET_TYPES = ["message", "review", "listing", "user"] as const;
export type ReportTargetType = (typeof REPORT_TARGET_TYPES)[number];

export const REPORT_REASONS = [
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate", label: "Hate speech or symbols" },
  { value: "sexual", label: "Nudity or sexual content" },
  { value: "violence", label: "Violence or threats" },
  { value: "scam", label: "Scam, fraud, or spam" },
  { value: "illegal", label: "Illegal or unsafe goods" },
  { value: "other", label: "Something else" },
] as const;

export const REPORT_REASON_VALUES = REPORT_REASONS.map((r) => r.value);

// Objectionable-content filter. Deliberately conservative: catches unambiguous
// slurs and explicit terms so they can't be posted in chat or reviews, without
// trying to be a full moderation AI (the report + block + admin queue handle the
// judgment calls). Word-boundary matched on a normalized copy so simple
// obfuscation (sp a ces, l33t, punctuation) still trips it.
const BANNED_PATTERNS: RegExp[] = [
  /\bn[i1!|]gg(?:er|a)s?\b/,
  /\bf[a4@]gg?(?:ot|s)?\b/,
  /\bk[i1!]ke\b/,
  /\bch[i1!]nk\b/,
  /\bsp[i1!]c\b/,
  /\bc[o0]{2}n\b/,
  /\br[e3]t[a4]rd/,
  /\bcunt\b/,
  /\bwh[o0]re\b/,
  /\brap(?:e|ing|ist)\b/,
  /\bk[i1!]ll\s*(?:yo?u?rself|urself)\b/,
  /\bkys\b/,
];

function normalizeForFilter(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s@!|]/g, "")
    .replace(/\s{2,}/g, " ");
}

export function containsObjectionableContent(text: string | null | undefined): boolean {
  if (!text) return false;
  const normalized = normalizeForFilter(text);
  const collapsed = normalized.replace(/\s+/g, "");
  return BANNED_PATTERNS.some((re) => re.test(normalized) || re.test(collapsed));
}
